import Navbar from "@/components/layout/Navbar";
import Footer from "@/components/layout/Footer";
import { Button } from "@/components/ui/button";
import { Link } from "wouter";
import { 
  Users, 
  Heart, 
  Award, 
  BarChart4, 
  Book, 
  Star 
} from "lucide-react";

export default function AboutPage() {
  return (
    <div className="min-h-screen flex flex-col">
      <Navbar />
      <main className="flex-grow">
        {/* Hero Section */}
        <section className="bg-primary-50 py-16 px-4 sm:px-6 lg:px-8">
          <div className="max-w-7xl mx-auto">
            <div className="text-center">
              <h1 className="text-4xl font-extrabold text-primary-900 sm:text-5xl md:text-6xl">
                About StaffAbility
              </h1>
              <p className="mt-3 max-w-md mx-auto text-lg text-primary-700 sm:text-xl md:mt-5 md:max-w-3xl">
                Learn about our mission, values, and the people behind our organization dedicated to building futures.
              </p>
            </div>
          </div>
        </section>

        {/* Mission and Vision */}
        <section className="py-16 px-4 sm:px-6 lg:px-8 bg-white">
          <div className="max-w-7xl mx-auto">
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
              <div>
                <h2 className="text-3xl font-bold text-slate-900">Our Mission</h2>
                <div className="mt-6 prose prose-primary text-slate-600">
                  <p>
                    StaffAbility is dedicated to helping individuals overcome significant life barriers by providing access to job opportunities, career training, housing support, education, and one-on-one case worker mentorship.
                  </p>
                  <p>
                    We believe in the power of dignified employment to transform lives and create lasting change. By focusing on the whole person and addressing multiple barriers simultaneously, we create pathways to stability and success.
                  </p>
                </div>
                <div className="mt-8">
                  <h3 className="text-xl font-semibold text-slate-900">Our Vision</h3>
                  <p className="mt-4 text-slate-600">
                    A community where everyone, regardless of past circumstances, has the opportunity to thrive through meaningful employment and supportive relationships.
                  </p>
                </div>
              </div>
              <div className="bg-white p-8 rounded-lg shadow-md">
                <h3 className="text-xl font-semibold text-slate-900 mb-6">Our Core Values</h3>
                <ul className="space-y-6">
                  <li className="flex">
                    <div className="flex-shrink-0">
                      <div className="flex items-center justify-center h-12 w-12 rounded-md bg-gradient-to-r from-amber-400 to-amber-600 text-white shadow-md">
                        <Heart className="h-6 w-6" />
                      </div>
                    </div>
                    <div className="ml-4">
                      <h4 className="text-lg font-medium text-slate-900">Dignity</h4>
                      <p className="mt-2 text-slate-600">We treat every individual with respect and affirm their inherent worth.</p>
                    </div>
                  </li>
                  <li className="flex">
                    <div className="flex-shrink-0">
                      <div className="flex items-center justify-center h-12 w-12 rounded-md bg-gradient-to-r from-sky-400 to-blue-600 text-white shadow-md">
                        <Users className="h-6 w-6" />
                      </div>
                    </div>
                    <div className="ml-4">
                      <h4 className="text-lg font-medium text-slate-900">Collaboration</h4>
                      <p className="mt-2 text-slate-600">We partner with employers, service providers, and community organizations.</p>
                    </div>
                  </li>
                  <li className="flex">
                    <div className="flex-shrink-0">
                      <div className="flex items-center justify-center h-12 w-12 rounded-md bg-gradient-to-r from-emerald-400 to-green-600 text-white shadow-md">
                        <Award className="h-6 w-6" />
                      </div>
                    </div>
                    <div className="ml-4">
                      <h4 className="text-lg font-medium text-slate-900">Second Chances</h4>
                      <p className="mt-2 text-slate-600">We believe in the potential for growth and change in every person.</p>
                    </div>
                  </li>
                </ul>
              </div>
            </div>
          </div>
        </section>

        {/* Our Story */}
        <section className="py-16 px-4 sm:px-6 lg:px-8 bg-primary-50">
          <div className="max-w-7xl mx-auto">
            <div className="text-center mb-12">
              <h2 className="text-3xl font-bold text-slate-900">Our Story</h2>
              <p className="mt-4 max-w-2xl mx-auto text-xl text-slate-600">
                From a small idea to a community-changing organization
              </p>
            </div>
            <div className="bg-white shadow-lg rounded-lg overflow-hidden">
              <div className="p-8">
                <div className="prose prose-primary max-w-none">
                  <p>
                    StaffAbility was founded in 2015 by a group of professionals who saw firsthand the challenges faced by individuals with barriers to employment. Our founder, Sarah Johnson, was working as a social worker when she noticed a critical gap in services for her clients.
                  </p>
                  <p>
                    "Many of my clients had the motivation and skills to work, but faced significant obstacles in finding employers willing to give them a chance," Sarah recalls. "At the same time, local businesses were struggling to find reliable, committed employees."
                  </p>
                  <p>
                    What began as a small pilot program with five clients and two employer partners has grown into a comprehensive organization serving hundreds of individuals each year. Our expansion has been driven by our commitment to addressing the multiple, overlapping barriers that prevent individuals from achieving employment stability.
                  </p>
                  <p>
                    Today, StaffAbility offers a holistic approach to employment services, combining job readiness training, supportive services, and strong employer partnerships. Our team of dedicated professionals works tirelessly to create opportunities for transformation and growth.
                  </p>
                  <p>
                    Through each person's success story, we are reminded of the power of meaningful work to change lives, restore dignity, and strengthen our community.
                  </p>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* Leadership Team */}
        <section className="py-16 px-4 sm:px-6 lg:px-8 bg-white">
          <div className="max-w-7xl mx-auto">
            <div className="text-center mb-12">
              <h2 className="text-3xl font-bold text-slate-900">Our Leadership Team</h2>
              <p className="mt-4 max-w-2xl mx-auto text-xl text-slate-600">
                Meet the dedicated professionals guiding our mission
              </p>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
              {/* Leader 1 */}
              <div className="bg-white shadow-md rounded-lg overflow-hidden">
                <div className="h-64 bg-slate-200">
                  <img 
                    className="w-full h-full object-cover" 
                    src="https://images.unsplash.com/photo-1573497019940-1c28c88b4f3e?ixlib=rb-1.2.1&auto=format&fit=crop&w=800&q=80" 
                    alt="Sarah Johnson, Executive Director" 
                  />
                </div>
                <div className="p-6">
                  <h3 className="text-xl font-semibold text-slate-900">Sarah Johnson</h3>
                  <p className="text-primary-600">Executive Director & Founder</p>
                  <p className="mt-3 text-slate-600">
                    Former social worker with 15+ years experience in workforce development and social services.
                  </p>
                </div>
              </div>
              
              {/* Leader 2 */}
              <div className="bg-white shadow-md rounded-lg overflow-hidden">
                <div className="h-64 bg-slate-200">
                  <img 
                    className="w-full h-full object-cover" 
                    src="https://images.unsplash.com/photo-1519085360753-af0119f7cbe7?ixlib=rb-1.2.1&auto=format&fit=crop&w=800&q=80" 
                    alt="Michael Chen, Operations Director" 
                  />
                </div>
                <div className="p-6">
                  <h3 className="text-xl font-semibold text-slate-900">Michael Chen</h3>
                  <p className="text-primary-600">Operations Director</p>
                  <p className="mt-3 text-slate-600">
                    Business operations expert who previously worked in corporate social responsibility programs.
                  </p>
                </div>
              </div>
              
              {/* Leader 3 */}
              <div className="bg-white shadow-md rounded-lg overflow-hidden">
                <div className="h-64 bg-slate-200">
                  <img 
                    className="w-full h-full object-cover" 
                    src="https://images.unsplash.com/photo-1508214751196-bcfd4ca60f91?ixlib=rb-1.2.1&auto=format&fit=crop&w=800&q=80" 
                    alt="Aisha Williams, Program Director" 
                  />
                </div>
                <div className="p-6">
                  <h3 className="text-xl font-semibold text-slate-900">Aisha Williams</h3>
                  <p className="text-primary-600">Program Director</p>
                  <p className="mt-3 text-slate-600">
                    Former case manager with expertise in mental health and substance abuse recovery support.
                  </p>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* Impact Stats */}
        <section className="py-16 px-4 sm:px-6 lg:px-8 bg-primary-700">
          <div className="max-w-7xl mx-auto">
            <div className="text-center mb-12">
              <h2 className="text-3xl font-bold text-black">Our Impact</h2>
              <p className="mt-4 max-w-2xl mx-auto text-xl text-gray-900">
                Measuring our success through lives changed
              </p>
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-8">
              <div className="text-center">
                <div className="inline-flex items-center justify-center h-16 w-16 rounded-full bg-primary-900 text-primary-100 mb-4">
                  <Users className="h-8 w-8" />
                </div>
                <p className="text-4xl font-bold text-black">500+</p>
                <p className="text-xl font-medium text-gray-900">Clients Served</p>
              </div>
              
              <div className="text-center">
                <div className="inline-flex items-center justify-center h-16 w-16 rounded-full bg-primary-900 text-primary-100 mb-4">
                  <BarChart4 className="h-8 w-8" />
                </div>
                <p className="text-4xl font-bold text-black">75%</p>
                <p className="text-xl font-medium text-gray-900">Job Placement Rate</p>
              </div>
              
              <div className="text-center">
                <div className="inline-flex items-center justify-center h-16 w-16 rounded-full bg-primary-900 text-primary-100 mb-4">
                  <Book className="h-8 w-8" />
                </div>
                <p className="text-4xl font-bold text-black">83%</p>
                <p className="text-xl font-medium text-gray-900">Retention Rate</p>
              </div>
              
              <div className="text-center">
                <div className="inline-flex items-center justify-center h-16 w-16 rounded-full bg-primary-900 text-primary-100 mb-4">
                  <Star className="h-8 w-8" />
                </div>
                <p className="text-4xl font-bold text-black">75+</p>
                <p className="text-xl font-medium text-gray-900">Employer Partners</p>
              </div>
            </div>
          </div>
        </section>

        {/* Call to Action */}
        <section className="py-16 px-4 sm:px-6 lg:px-8 bg-white">
          <div className="max-w-7xl mx-auto">
            <div className="bg-primary-50 rounded-lg shadow-md p-8 md:p-12">
              <div className="text-center">
                <h2 className="text-3xl font-bold text-slate-900">Join Our Mission</h2>
                <p className="mt-4 max-w-2xl mx-auto text-xl text-slate-700">
                  Whether you're looking for help, want to hire our clients, or support our mission, we'd love to connect with you.
                </p>
                <div className="mt-8 flex flex-col sm:flex-row justify-center gap-4">
                  <Link href="/get-help">
                    <Button size="lg" className="w-full sm:w-auto">Get Help</Button>
                  </Link>
                  <Link href="/hire-from-us">
                    <Button variant="outline" size="lg" className="w-full sm:w-auto">Hire Talent</Button>
                  </Link>
                  <Link href="/get-involved">
                    <Button variant="secondary" size="lg" className="w-full sm:w-auto">Support Us</Button>
                  </Link>
                </div>
              </div>
            </div>
          </div>
        </section>
      </main>
      <Footer />
    </div>
  );
}
